import type { ShopperBasketsTypes, ShopperProductsTypes } from 'commerce-sdk-isomorphic'

export const SHIPPING = 'shipping' as const
export const BILLING = 'billing' as const
export const TERMS = 'termsAndConditions' as const
export const CONTACT = 'contact' as const
export type AddressFormType = typeof SHIPPING | typeof BILLING

export const localisedPcpData = (product?: any) => {
  const selectedPcpOptionId = product?.optionItems?.find(
    (option: { optionId: string }) => option?.optionId === 'pcp',
  )?.optionValueId
  const pcpOption = product?.options?.find((option: any) => option?.id === 'pcp')
  const pcpOptionValue = pcpOption?.values?.find((value: any) => value?.id === selectedPcpOptionId)
  return selectedPcpOptionId
    ? {
        id: pcpOptionValue?.id || '',
        name: pcpOption?.name || '',
        term: pcpOptionValue?.name || '',
        price: pcpOptionValue?.price || undefined,
      }
    : undefined
}

export const areAllProductsAvailable = (cart: BasketWithProductData, stock?: StockResult) =>
  stock?.stores?.[0]?.stockLevels?.every((stockLevel) => {
    if (stockLevel.quantity <= 0) return false
    const quantity =
      cart.productItems?.filter((p) => p.productId === stockLevel.sku).reduce((sum, p) => sum + (p.quantity || 0), 0) ||
      0
    return quantity <= stockLevel.quantity
  })

// Under the bopis switch the shared item rules no longer block; a line only
// blocks when the store-first allocation leaves it uncovered by both pools.
export const validateClickAndCollect = (
  cart: BasketWithProductData,
  store?: SFCCStore,
  stock?: StockResult,
  bopisEnabled = false,
) => {
  const result = {
    ok: true,
    storeError: false,
    nonCncStore: false,
    productError: '',
  }

  if (!store) {
    result.storeError = true
  } else if (!store.c_clickAndCollectEnabled) {
    result.nonCncStore = true
  } else {
    const products = cart.productItems || []
    const stockLevels = stock?.stores?.[0]?.stockLevels
    for (const product of products) {
      const reason = bopisEnabled
        ? resolveCartLineCollect(collectViewFromCartItem(product), {
            storefrontInStock: (product.inventory?.ats ?? 0) > 0,
            stockLine: stockLevels?.find((stockLevel) => stockLevel.sku === product.productId),
            itemId: product.itemId,
            stockConsumingLineIds: stockConsumingLineIds(cart, product.productId),
          }).block
        : cartItemBlock(collectViewFromCartItem(product))
      if (reason) {
        result.productError = reason
        break
      }
    }

    if (!bopisEnabled && !result.productError && !areAllProductsAvailable(cart, stock))
      result.productError = 'outOfStock'
  }
  result.ok = !result.productError && !result.storeError && !result.nonCncStore
  return result
}

export const isShippingValid = (cart: ShopperBasketsTypes.Basket, store?: SFCCStore, stock?: StockResult) => {
  const CLICK_AND_COLLECT_NAME = useNuxtApp().$i18n.t('checkout.order_summary.click_and_collect')
  const shippingAddress = cart?.shipments?.[0]?.shippingAddress
  const shippingMethod = cart?.shipments?.[0]?.shippingMethod
  const isClickAndCollect = shippingMethod?.id === CLICK_AND_COLLECT_ID

  if (!shippingAddress?.phone || !shippingMethod) {
    return false
  }

  if (!isClickAndCollect && shippingAddress.firstName === CLICK_AND_COLLECT_NAME) {
    return false
  }

  const { productError, storeError, nonCncStore } = validateClickAndCollect(
    cart,
    store,
    stock,
    useFeatureFlags().bopisEnabled(),
  )
  if (
    isClickAndCollect &&
    (productError ||
      storeError ||
      nonCncStore ||
      !cart.c_clickAndCollectStoreID ||
      cart.c_clickAndCollectStoreID !== store?.id)
  ) {
    return false
  }

  return true
}

export const hasEngravedProducts = (cart: BasketWithProductData) => {
  return cart.productItems?.some((item) => !!item?.c_engravingText?.trim())
}

export const isCartIntact = (cart: ShopperBasketsTypes.Basket, oldCart: ShopperBasketsTypes.Basket) => {
  if (cart.orderTotal !== oldCart.orderTotal) return false
  if (cart.productItems?.length !== oldCart.productItems?.length) return false

  const cartItems = cart.productItems || []
  const oldCartItems = oldCart.productItems || []
  const cartItemsMap = new Map<string, number>()
  const oldCartItemsMap = new Map<string, number>()

  // Count of each productId
  cartItems.forEach((item) => {
    const productId = item.productId!
    cartItemsMap.set(productId, (cartItemsMap.get(productId) || 0) + 1)
  })

  oldCartItems.forEach((item) => {
    const productId = item.productId!
    oldCartItemsMap.set(productId, (oldCartItemsMap.get(productId) || 0) + 1)
  })

  if (cartItemsMap.size !== oldCartItemsMap.size) return false

  for (const [productId, count] of cartItemsMap) {
    if (oldCartItemsMap.get(productId) !== count) return false
  }

  return true
}

export const isGiftCardsStillValid = async (cart: ShopperBasketsTypes.Basket) => {
  const giftCardPIs = cart.paymentInstruments?.filter((pi) => pi.paymentMethodId === PAYMENT_METHOD_IDS.givex)
  if (!giftCardPIs?.length) {
    return true
  }

  return await useGivex().prePaymentCheck(cart.basketId)
}

export const mapStoreAddress = (
  firstName: string,
  store?: SFCCStore,
  countryCode?: string,
  phoneOverride?: string,
): ShopperBasketsTypes.OrderAddress => {
  const states = countryCode === 'CA' ? STATES_CA : STATES_AU
  const stateCode = states.find(({ name }) => store?.stateCode === name)?.code || store?.stateCode
  const phone = phoneOverride ?? useCheckoutStore().forms[CONTACT].veeForm.values.phone

  return {
    firstName,
    lastName: store?.name,
    address1: store?.address1,
    address2: store?.address2,
    city: store?.city,
    phone,
    postalCode: store?.postalCode,
    stateCode,
    countryCode: store?.countryCode || countryCode,
  }
}

export const isAddressSame = (addr1: ShopperBasketsTypes.OrderAddress, addr2: ShopperBasketsTypes.OrderAddress) => {
  const fieldsToCheck = [
    'firstName',
    'lastName',
    'address1',
    'address2',
    'city',
    'stateCode',
    'postalCode',
    'countryCode',
    'phone',
  ]

  for (const field of fieldsToCheck) {
    // All fields are string & optional, so undefined and empty string are equivalent
    if ((addr1[field] || addr2[field]) && addr1[field] !== addr2[field]) {
      return false
    }
  }
  return true
}

export const findProductAmountInCart = (cart: BasketWithProductData, productSku?: string) => {
  const filteredProductItems = cart?.productItems?.filter((product) => product.productId === productSku)
  return filteredProductItems?.length ?? 0
}

export function findPrimaryProductImage(product: FlattenedProduct | undefined) {
  if (product) {
    const suffixPreferred = '-21.jpg'
    const suffixFallback = '-1.jpg'
    const images = product?.imageGroups?.[0]?.images
    let productImage = images?.find((image) => image.disBaseLink?.endsWith(suffixPreferred))
    if (!productImage) {
      productImage = images?.find((image) => image.disBaseLink?.endsWith(suffixFallback))
    }

    if (productImage) {
      return { url: productImage.disBaseLink, alt: productImage.alt }
    }
  }
  return { url: '', alt: '' }
}

const getProductCustomAttributes = (product: ShopperProductsTypes.Product) =>
  Object.keys(product).reduce<Record<string, any>>((acc, key) => {
    if (key.startsWith('c_')) {
      acc[key] = product[key as keyof typeof product]
    }
    return acc
  }, {})

export const mergeProductItemsWithProductData = async (productItems: ShopperBasketsTypes.ProductItem[]) => {
  const ids = productItems?.map((productItem) => productItem.productId) as string[]
  if (ids?.length) {
    const fetchedProducts = await useProducts().getProducts(ids)
    if (Array.isArray(fetchedProducts)) {
      const productItemsWithData: ProductItemWithProductData[] = productItems?.map((productItem) => {
        const product = fetchedProducts?.find((p) => p.id === productItem.productId)
        if (!product) return {}

        const brand = product.brand
        const image = findPrimaryProductImage(product)
        const productCustomAttributes = getProductCustomAttributes(product)
        const inventory = product.inventory
        const productPrices = product.tieredPrices
        const variant = product.variants?.find((variant) => variant.productId === productItem.productId)
        const variationAttributes = product?.variationAttributes
        const variationValues = product?.variationValues
        const variationGroups = product?.variationGroups
        const masterId = product.master?.masterId
        const productPromotions = product?.productPromotions
        const options = product?.options
        const productName = product?.name

        return {
          ...productItem,
          brand,
          image,
          inventory,
          productCustomAttributes,
          productPrices,
          variant,
          variationAttributes,
          variationValues,
          variationGroups,
          masterId,
          productPromotions,
          options,
          productName,
        }
      })
      return productItemsWithData
    }
  }
}

export const scrollToError = (el?: Element | null) => {
  if (el) {
    el.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    })
  }
}
